/**
 * Gamification Service - Phase 4
 * 
 * Provides streak tracking, XP calculations, level progression, and badge unlocking.
 * Follows safety rules: isolated additions, no breaking changes.
 * 
 * @module gamificationService
 */

import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '@/config/firebase';

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

/**
 * XP rewards for different actions
 */
export const XP_REWARDS = {
  TASK_COMPLETE: 10,
  TASK_COMPLETE_ON_TIME: 15,      // Bonus for completing before due date
  TASK_COMPLETE_EARLY: 20,         // Bonus for completing way ahead
  STREAK_DAY_BONUS: 5,             // Per day of streak
  STREAK_WEEK_BONUS: 50,           // Weekly streak milestone
  FIRST_TASK_OF_DAY: 5,            // Bonus for first task
  ALL_TASKS_COMPLETE: 25,          // Cleared all tasks for the day
  BUDGET_GOAL_MET: 30,             // Met budget target
  SAVINGS_CONTRIBUTION: 15,        // Added to savings
  TRANSACTION_LOGGED: 5,           // Logged a transaction
};

/**
 * Level thresholds (cumulative XP required)
 */
export const LEVELS = [
  { level: 1, xpRequired: 0, title: 'Beginner' },
  { level: 2, xpRequired: 100, title: 'Starter' },
  { level: 3, xpRequired: 250, title: 'Planner' },
  { level: 4, xpRequired: 500, title: 'Organizer' },
  { level: 5, xpRequired: 1000, title: 'Achiever' },
  { level: 6, xpRequired: 2000, title: 'Pro' },
  { level: 7, xpRequired: 3500, title: 'Expert' },
  { level: 8, xpRequired: 5500, title: 'Master' },
  { level: 9, xpRequired: 8000, title: 'Champion' },
  { level: 10, xpRequired: 12000, title: 'Legend' },
];

/**
 * Badge definitions
 */
export const BADGES = {
  // Streak badges
  STREAK_3: {
    id: 'streak_3',
    name: 'Getting Started',
    description: 'Complete tasks 3 days in a row',
    icon: '🔥',
    category: 'streak',
    requirement: { type: 'streak', value: 3 },
  },
  STREAK_7: {
    id: 'streak_7',
    name: 'Week Warrior',
    description: 'Complete tasks 7 days in a row',
    icon: '⚡',
    category: 'streak',
    requirement: { type: 'streak', value: 7 },
  },
  STREAK_14: {
    id: 'streak_14',
    name: 'Two Week Champion',
    description: 'Complete tasks 14 days in a row',
    icon: '💪',
    category: 'streak',
    requirement: { type: 'streak', value: 14 },
  },
  STREAK_30: {
    id: 'streak_30',
    name: 'Monthly Master',
    description: 'Complete tasks 30 days in a row',
    icon: '👑',
    category: 'streak',
    requirement: { type: 'streak', value: 30 },
  },

  // Task completion badges
  TASKS_10: {
    id: 'tasks_10',
    name: 'Task Tackler',
    description: 'Complete 10 tasks',
    icon: '✅',
    category: 'tasks',
    requirement: { type: 'tasksCompleted', value: 10 },
  },
  TASKS_50: {
    id: 'tasks_50',
    name: 'Productivity Pro',
    description: 'Complete 50 tasks',
    icon: '🎯',
    category: 'tasks',
    requirement: { type: 'tasksCompleted', value: 50 },
  },
  TASKS_100: {
    id: 'tasks_100',
    name: 'Century Club',
    description: 'Complete 100 tasks',
    icon: '💯',
    category: 'tasks',
    requirement: { type: 'tasksCompleted', value: 100 },
  },
  TASKS_500: {
    id: 'tasks_500',
    name: 'Task Legend',
    description: 'Complete 500 tasks',
    icon: '🏆',
    category: 'tasks',
    requirement: { type: 'tasksCompleted', value: 500 },
  },

  // Financial badges
  FIRST_BUDGET: {
    id: 'first_budget',
    name: 'Budget Beginner',
    description: 'Create your first budget',
    icon: '📊',
    category: 'financial',
    requirement: { type: 'budgetsCreated', value: 1 },
  },
  FIRST_SAVINGS: {
    id: 'first_savings',
    name: 'Saver Starter',
    description: 'Make your first savings contribution',
    icon: '🐷',
    category: 'financial',
    requirement: { type: 'savingsContributions', value: 1 },
  },
  TRANSACTIONS_100: {
    id: 'transactions_100',
    name: 'Transaction Tracker',
    description: 'Log 100 transactions',
    icon: '📝',
    category: 'financial',
    requirement: { type: 'transactionsLogged', value: 100 },
  },

  // Level badges
  LEVEL_5: {
    id: 'level_5',
    name: 'Rising Star',
    description: 'Reach level 5',
    icon: '⭐',
    category: 'level',
    requirement: { type: 'level', value: 5 },
  },
  LEVEL_10: {
    id: 'level_10',
    name: 'Legendary Status',
    description: 'Reach level 10',
    icon: '🌟',
    category: 'level',
    requirement: { type: 'level', value: 10 },
  },

  // Special badges
  EARLY_BIRD: {
    id: 'early_bird',
    name: 'Early Bird',
    description: 'Complete a task before 7 AM',
    icon: '🌅',
    category: 'special',
    requirement: { type: 'special', value: 'earlyBird' },
  },
  NIGHT_OWL: {
    id: 'night_owl',
    name: 'Night Owl',
    description: 'Complete a task after 11 PM',
    icon: '🦉',
    category: 'special',
    requirement: { type: 'special', value: 'nightOwl' },
  },
  PERFECT_WEEK: {
    id: 'perfect_week',
    name: 'Perfect Week',
    description: 'Complete all tasks for 7 days straight',
    icon: '💎',
    category: 'special',
    requirement: { type: 'special', value: 'perfectWeek' },
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get the start of today (midnight)
 */
const getStartOfDay = (date = new Date()) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Check if two dates are on the same day
 */
const isSameDay = (date1, date2) => {
  const d1 = getStartOfDay(date1);
  const d2 = getStartOfDay(date2);
  return d1.getTime() === d2.getTime();
};

/**
 * Check if date1 is exactly one day before date2
 */
const isConsecutiveDay = (date1, date2) => {
  const d1 = getStartOfDay(date1);
  const d2 = getStartOfDay(date2);
  const diffMs = d2.getTime() - d1.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays === 1;
};

/**
 * Calculate level from total XP
 */
export const calculateLevel = (totalXP) => {
  let currentLevel = LEVELS[0];
  for (const level of LEVELS) {
    if (totalXP >= level.xpRequired) {
      currentLevel = level;
    } else {
      break;
    }
  }
  return currentLevel;
};

/**
 * Calculate XP progress to next level
 */
export const calculateLevelProgress = (totalXP) => {
  const currentLevel = calculateLevel(totalXP);
  const currentLevelIndex = LEVELS.findIndex(l => l.level === currentLevel.level);
  const nextLevel = LEVELS[currentLevelIndex + 1];
  
  if (!nextLevel) {
    return { current: totalXP, required: totalXP, percentage: 100, isMaxLevel: true };
  }

  const xpIntoCurrentLevel = totalXP - currentLevel.xpRequired;
  const xpNeededForNextLevel = nextLevel.xpRequired - currentLevel.xpRequired;
  const percentage = Math.round((xpIntoCurrentLevel / xpNeededForNextLevel) * 100);

  return {
    current: xpIntoCurrentLevel,
    required: xpNeededForNextLevel,
    percentage: Math.min(percentage, 100),
    isMaxLevel: false,
  };
};

// ============================================================================
// FIRESTORE OPERATIONS
// ============================================================================

/**
 * Get default gamification stats
 */
const getDefaultStats = () => ({
  totalXP: 0,
  currentStreak: 0,
  longestStreak: 0,
  lastActivityDate: null,
  tasksCompletedToday: 0,
  tasksCompletedTotal: 0,
  budgetsCreated: 0,
  savingsContributions: 0,
  transactionsLogged: 0,
  earnedBadges: [],
  perfectWeekDays: 0,
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
});

/**
 * Get user's gamification stats from Firestore
 */
export const getUserStats = async (userId) => {
  if (!userId) {
    console.warn('[Gamification] No userId provided');
    return getDefaultStats();
  }

  try {
    const docRef = doc(db, 'users', userId, 'gamification', 'stats');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // Convert Firestore Timestamp to Date if needed
      return {
        ...data,
        lastActivityDate: data.lastActivityDate?.toDate?.() || data.lastActivityDate,
      };
    }

    // Initialize stats for new user
    const defaultStats = getDefaultStats();
    await setDoc(docRef, defaultStats);
    return defaultStats;
  } catch (error) {
    console.error('[Gamification] Error fetching stats:', error);
    return getDefaultStats();
  }
};

/**
 * Update user's gamification stats
 */
export const updateUserStats = async (userId, updates) => {
  if (!userId) {
    console.warn('[Gamification] No userId provided for update');
    return null;
  }

  try {
    const docRef = doc(db, 'users', userId, 'gamification', 'stats');
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    // If doc doesn't exist, create it
    if (error.code === 'not-found') {
      const defaultStats = getDefaultStats();
      await setDoc(doc(db, 'users', userId, 'gamification', 'stats'), {
        ...defaultStats,
        ...updates,
      });
      return true;
    }
    console.error('[Gamification] Error updating stats:', error);
    return false;
  }
};

// ============================================================================
// STREAK MANAGEMENT
// ============================================================================

/**
 * Update streak based on task completion
 * Returns updated streak info and any new badges earned
 */
export const updateStreak = async (userId, stats) => {
  const now = new Date();
  const today = getStartOfDay(now);
  const lastActivity = stats.lastActivityDate ? new Date(stats.lastActivityDate) : null;

  let newStreak = stats.currentStreak || 0;
  let isNewDay = false;

  if (!lastActivity) {
    // First ever activity
    newStreak = 1;
    isNewDay = true;
  } else if (isSameDay(lastActivity, today)) {
    // Already active today, streak continues but no increment
    isNewDay = false;
  } else if (isConsecutiveDay(lastActivity, today)) {
    // Consecutive day - increment streak
    newStreak = (stats.currentStreak || 0) + 1;
    isNewDay = true;
  } else {
    // Streak broken - reset to 1
    newStreak = 1;
    isNewDay = true;
  }

  const longestStreak = Math.max(newStreak, stats.longestStreak || 0);

  return {
    currentStreak: newStreak,
    longestStreak,
    lastActivityDate: Timestamp.fromDate(now),
    isNewDay,
    tasksCompletedToday: isNewDay ? 1 : (stats.tasksCompletedToday || 0) + 1,
  };
};

// ============================================================================
// XP & REWARDS
// ============================================================================

/**
 * Calculate XP reward for completing a task
 */
export const calculateTaskXP = (task, stats) => {
  let xp = XP_REWARDS.TASK_COMPLETE;
  const reasons = ['Task completed'];

  // Bonus for completing on time
  if (task.dueDate) {
    const dueDate = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
    const now = new Date();
    const daysEarly = Math.floor((dueDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysEarly >= 3) {
      xp += XP_REWARDS.TASK_COMPLETE_EARLY - XP_REWARDS.TASK_COMPLETE;
      reasons.push('Completed early');
    } else if (daysEarly >= 0) {
      xp += XP_REWARDS.TASK_COMPLETE_ON_TIME - XP_REWARDS.TASK_COMPLETE;
      reasons.push('On time');
    }
  }

  // First task of the day bonus
  if (stats.tasksCompletedToday === 0) {
    xp += XP_REWARDS.FIRST_TASK_OF_DAY;
    reasons.push('First task of day');
  }

  // Streak bonus
  if (stats.currentStreak > 1) {
    const streakBonus = Math.min(stats.currentStreak * XP_REWARDS.STREAK_DAY_BONUS, 50);
    xp += streakBonus;
    reasons.push(`${stats.currentStreak} day streak`);
  }

  // Weekly milestone bonus
  if (stats.currentStreak > 0 && stats.currentStreak % 7 === 0) {
    xp += XP_REWARDS.STREAK_WEEK_BONUS;
    reasons.push('Weekly milestone');
  }

  return { xp, reasons };
};

/**
 * Award XP for a specific action
 */
export const awardXP = async (userId, action, context = {}) => {
  const stats = await getUserStats(userId);
  let xpEarned = 0;
  let reasons = [];
  let newBadges = [];

  switch (action) {
    case 'TASK_COMPLETE': {
      const taskReward = calculateTaskXP(context.task || {}, stats);
      xpEarned = taskReward.xp;
      reasons = taskReward.reasons;
      break;
    }
    case 'BUDGET_GOAL_MET':
      xpEarned = XP_REWARDS.BUDGET_GOAL_MET;
      reasons = ['Budget goal achieved'];
      break;
    case 'SAVINGS_CONTRIBUTION':
      xpEarned = XP_REWARDS.SAVINGS_CONTRIBUTION;
      reasons = ['Savings contribution'];
      break;
    case 'TRANSACTION_LOGGED':
      xpEarned = XP_REWARDS.TRANSACTION_LOGGED;
      reasons = ['Transaction logged'];
      break;
    case 'ALL_TASKS_COMPLETE':
      xpEarned = XP_REWARDS.ALL_TASKS_COMPLETE;
      reasons = ['All tasks completed'];
      break;
    default:
      console.warn('[Gamification] Unknown action:', action);
      return { xpEarned: 0, reasons: [], newBadges: [] };
  }

  const newTotalXP = (stats.totalXP || 0) + xpEarned;
  const oldLevel = calculateLevel(stats.totalXP || 0);
  const newLevel = calculateLevel(newTotalXP);

  // Check for level up
  const leveledUp = newLevel.level > oldLevel.level;

  // Update stats
  await updateUserStats(userId, { totalXP: newTotalXP });

  // Check for new badges based on updated stats
  newBadges = await checkAndAwardBadges(userId, {
    ...stats,
    totalXP: newTotalXP,
  });

  return {
    xpEarned,
    totalXP: newTotalXP,
    reasons,
    newBadges,
    leveledUp,
    oldLevel,
    newLevel,
  };
};

// ============================================================================
// BADGE MANAGEMENT
// ============================================================================

/**
 * Check if user qualifies for any new badges
 */
export const checkAndAwardBadges = async (userId, stats) => {
  const earnedBadges = stats.earnedBadges || [];
  const newBadges = [];

  for (const [key, badge] of Object.entries(BADGES)) {
    // Skip already earned badges
    if (earnedBadges.includes(badge.id)) continue;

    let earned = false;
    const req = badge.requirement;

    switch (req.type) {
      case 'streak':
        earned = stats.currentStreak >= req.value;
        break;
      case 'tasksCompleted':
        earned = stats.tasksCompletedTotal >= req.value;
        break;
      case 'budgetsCreated':
        earned = stats.budgetsCreated >= req.value;
        break;
      case 'savingsContributions':
        earned = stats.savingsContributions >= req.value;
        break;
      case 'transactionsLogged':
        earned = stats.transactionsLogged >= req.value;
        break;
      case 'level': {
        const level = calculateLevel(stats.totalXP);
        earned = level.level >= req.value;
        break;
      }
      case 'special':
        // Special badges are awarded through specific triggers
        earned = stats[`special_${req.value}`] === true;
        break;
    }

    if (earned) {
      newBadges.push(badge);
    }
  }

  // Save new badges to Firestore
  if (newBadges.length > 0) {
    const allEarnedBadges = [...earnedBadges, ...newBadges.map(b => b.id)];
    await updateUserStats(userId, { earnedBadges: allEarnedBadges });
  }

  return newBadges;
};

/**
 * Check for special time-based badges
 */
export const checkSpecialBadges = async (userId) => {
  const hour = new Date().getHours();
  const updates = {};

  if (hour < 7) {
    updates['special_earlyBird'] = true;
  }
  if (hour >= 23) {
    updates['special_nightOwl'] = true;
  }

  if (Object.keys(updates).length > 0) {
    await updateUserStats(userId, updates);
  }

  return updates;
};

// ============================================================================
// MAIN TASK COMPLETION HANDLER
// ============================================================================

/**
 * Process task completion with full gamification rewards
 * This is the main entry point called when a task is completed
 */
export const processTaskCompletion = async (userId, task) => {
  if (!userId) {
    console.warn('[Gamification] No userId for task completion');
    return null;
  }

  try {
    // Get current stats
    const stats = await getUserStats(userId);

    // Update streak
    const streakUpdate = await updateStreak(userId, stats);

    // Check special badges (time-based)
    await checkSpecialBadges(userId);

    // Calculate XP
    const xpResult = await awardXP(userId, 'TASK_COMPLETE', { task });

    // Update task counts
    const updatedStats = {
      ...streakUpdate,
      tasksCompletedTotal: (stats.tasksCompletedTotal || 0) + 1,
    };

    await updateUserStats(userId, updatedStats);

    // Check for any new badges
    const allStats = { ...stats, ...updatedStats, totalXP: xpResult.totalXP };
    const newBadges = await checkAndAwardBadges(userId, allStats);

    return {
      streakUpdate,
      xpResult: { ...xpResult, newBadges: [...(xpResult.newBadges || []), ...newBadges] },
      stats: allStats,
      shouldCelebrate: xpResult.leveledUp || newBadges.length > 0 || streakUpdate.currentStreak % 7 === 0,
    };
  } catch (error) {
    console.error('[Gamification] Error processing task completion:', error);
    return null;
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // Constants
  XP_REWARDS,
  LEVELS,
  BADGES,
  // Helpers
  calculateLevel,
  calculateLevelProgress,
  // Firestore
  getUserStats,
  updateUserStats,
  // Core functions
  updateStreak,
  awardXP,
  checkAndAwardBadges,
  processTaskCompletion,
};
